/**
 * /app/$tenantSlug/leave/calendar — Leave → Team calendar (wave 2).
 *
 * A month view of who is on leave or working from home, for the VIEWER'S
 * scope: the same role-filtered lists the rest of the module uses
 * (GET /leave/requests and GET /wfh/requests are filtered in SQL — an
 * employee sees their own, a manager their reports (direct + skip-level)
 * and themselves, HR the whole workspace). No new backend endpoint: the
 * month is cut client-side from those lists.
 *
 *   - approved leave / WFH are solid chips; pending ones (toggle) dashed;
 *   - a half-day leave is labelled "½ AM" / "½ PM";
 *   - public holidays and the workspace weekly off are shaded;
 *   - optional (restricted) holidays are a day off only for whoever picked
 *     them: each pick in the viewer's scope is a chip ("Optional holiday",
 *     GET /holidays/picks), and the date is shaded for the viewer when they
 *     picked it themselves (docs/67 §6).
 *
 * Distinct from /calendar (the personal month with timesheet dots).
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { EmptyState } from "@/components/shared/empty-state";
import { useLeaveRequests, useLeaveSettings, useLeaveTypes } from "@/hooks/use-leave";
import { useWFHRequests } from "@/hooks/use-wfh";
import { useHolidays, useOptionalHolidayPicks } from "@/hooks/use-holidays";
import { useAuth } from "@/lib/auth";
import { toApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/leave/calendar",
)({
  component: LeaveTeamCalendarRoute,
});

function LeaveTeamCalendarRoute() {
  return (
    <TimeAndLeaveShell
      title="Who's Off"
      description="Who's on leave or working from home, month by month."
    >
      <LeaveFeatureGate>
        <TeamCalendar />
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}

// --- date helpers: YYYY-MM-DD strings, local calendar, no UTC parsing ---

function pad2(n: number) {
  return n < 10 ? `0${n}` : String(n);
}
function iso(y: number, m: number, d: number) {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}
function daysInMonth(y: number, m: number) {
  return new Date(y, m, 0).getDate();
}
/** Mon=0 … Sun=6 */
function mondayIndex(y: number, m: number, d: number) {
  return (new Date(y, m - 1, d).getDay() + 6) % 7;
}
/** Weekly-off bit (Mon=1 … Sun=64) of a date. */
function weekdayBit(y: number, m: number, d: number) {
  return 1 << mondayIndex(y, m, d);
}
const MONTHS = [
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface DayEntry {
  key: string;
  who: string;
  what: string;
  kind: "leave" | "wfh" | "optional";
  pending: boolean;
}

function TeamCalendar() {
  const now = new Date();
  const [focus, setFocus] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [showPending, setShowPending] = useState(true);
  const { user } = useAuth();
  const { isLeaveAdmin, role } = useLeaveRoles();

  const leaveQ = useLeaveRequests();
  const wfhQ = useWFHRequests();
  const typesQ = useLeaveTypes();
  const settingsQ = useLeaveSettings();
  const holidaysQ = useHolidays({ year: focus.y });
  const picksQ = useOptionalHolidayPicks(focus.y);

  const monthStart = iso(focus.y, focus.m, 1);
  const monthEnd = iso(focus.y, focus.m, daysInMonth(focus.y, focus.m));
  const weeklyOff = settingsQ.data?.weekly_off_mask ?? 64;

  const typeCode = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of typesQ.data?.leave_types ?? []) m.set(t.id, t.code);
    return m;
  }, [typesQ.data]);

  // date → entries for the focused month.
  const byDate = useMemo(() => {
    const out = new Map<string, DayEntry[]>();
    const push = (date: string, e: DayEntry) => {
      const list = out.get(date) ?? [];
      list.push(e);
      out.set(date, list);
    };
    const nameOf = (id: number, name?: string) =>
      id === user?.user_id ? "You" : (name ?? `User #${id}`);
    const eachDay = (s: string, e: string, fn: (d: string) => void) => {
      const from = s < monthStart ? monthStart : s;
      const to = e > monthEnd ? monthEnd : e;
      if (from > to) return;
      const [y, m] = [focus.y, focus.m];
      for (let d = Number(from.slice(8, 10)); d <= Number(to.slice(8, 10)); d++) {
        fn(iso(y, m, d));
      }
    };
    for (const r of leaveQ.data?.leave_requests ?? []) {
      const pending = r.status === "pending";
      if (r.status !== "approved" && !(showPending && pending)) continue;
      const half = r.half_day === "first_half" ? " ½ AM" : r.half_day === "second_half" ? " ½ PM" : "";
      eachDay(r.start_date, r.end_date, (d) =>
        push(d, {
          key: `l${r.id}-${d}`,
          who: nameOf(r.user_id, r.user_name),
          what: `${typeCode.get(r.leave_type_id) ?? "Leave"}${half}`,
          kind: "leave",
          pending,
        }),
      );
    }
    for (const w of wfhQ.data?.wfh_requests ?? []) {
      const pending = w.status === "pending";
      if (w.status !== "approved" && !(showPending && pending)) continue;
      const wHalf = w.half_day === "first_half" ? " ½ AM" : w.half_day === "second_half" ? " ½ PM" : "";
      eachDay(w.start_date, w.end_date, (d) =>
        push(d, { key: `w${w.id}-${d}`, who: nameOf(w.user_id, w.user_name), what: `WFH${wHalf}`, kind: "wfh", pending }),
      );
    }
    // A picked optional holiday is a day off for that person only.
    for (const p of picksQ.data?.picks ?? []) {
      const d = p.holiday_date.slice(0, 10);
      if (d < monthStart || d > monthEnd) continue;
      push(d, {
        key: `o${p.holiday_id}-${p.user_id}`,
        who: nameOf(p.user_id, p.user_name),
        what: "Optional holiday",
        kind: "optional",
        pending: false,
      });
    }
    return out;
  }, [leaveQ.data, wfhQ.data, picksQ.data, typeCode, showPending, monthStart, monthEnd, focus, user?.user_id]);

  const holidays = useMemo(() => {
    // Optional (restricted) holidays are a day off only for those who pick
    // them, so they are labelled but not shaded as a day off.
    // `picked` is the VIEWER's own pick: a day off for them, so shaded.
    const m = new Map<string, { name: string; optional: boolean; picked: boolean }>();
    for (const h of holidaysQ.data?.items ?? []) {
      if (!h.is_active) continue;
      const key = h.holiday_date.slice(0, 10);
      const prev = m.get(key);
      if (prev && !prev.optional) continue;
      m.set(key, { name: h.name, optional: !!h.is_optional, picked: !!h.picked });
    }
    return m;
  }, [holidaysQ.data]);

  const scopeLabel = isLeaveAdmin
    ? "Everyone in the workspace"
    : role === "manager"
      ? "You and the people who report to you"
      : "Your own leave and WFH";

  const loading = leaveQ.isLoading || wfhQ.isLoading;
  const error = leaveQ.error ?? wfhQ.error;
  const first = mondayIndex(focus.y, focus.m, 1);
  const total = daysInMonth(focus.y, focus.m);
  const cells: (number | null)[] = [
    ...Array.from({ length: first }, () => null),
    ...Array.from({ length: total }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const todayIso = iso(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const entriesThisMonth = [...byDate.values()].reduce((n, l) => n + l.length, 0);
  // Phone list: only the days with someone away or a holiday.
  const agendaDays = Array.from({ length: total }, (_, i) => i + 1)
    .map((d) => {
      const date = iso(focus.y, focus.m, d);
      return { d, date, entries: byDate.get(date) ?? [], holiday: holidays.get(date) };
    })
    .filter((x) => x.entries.length > 0 || x.holiday);

  const move = (delta: number) => {
    const d = new Date(focus.y, focus.m - 1 + delta, 1);
    setFocus({ y: d.getFullYear(), m: d.getMonth() + 1 });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Button variant="outline" size="icon" aria-label="Previous month" onClick={() => move(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="min-w-0 flex-1 text-center text-base font-semibold sm:min-w-40 sm:flex-none">
            {MONTHS[focus.m - 1]} {focus.y}
          </h2>
          <Button variant="outline" size="icon" aria-label="Next month" onClick={() => move(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFocus({ y: now.getFullYear(), m: now.getMonth() + 1 })}
          >
            Today
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:min-h-11">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input accent-primary"
            checked={showPending}
            onChange={(e) => setShowPending(e.target.checked)}
          />
          Show Pending Requests
        </label>
      </div>
      <p className="text-sm text-muted-foreground">Showing: {scopeLabel}.</p>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load the calendar</AlertTitle>
          <AlertDescription>{toApiError(error).message}</AlertDescription>
        </Alert>
      ) : loading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          {/* Phones: a day-by-day list of who is away. A 7-column grid at
              390 px showed only Mon–Thu, with Fri–Sun a sideways swipe. */}
          {agendaDays.length > 0 && (
            <ul className="space-y-2 sm:hidden" aria-label={`${MONTHS[focus.m - 1]} ${focus.y}`}>
              {agendaDays.map(({ d, date, entries, holiday }) => (
                <li
                  key={date}
                  className={cn(
                    "rounded-lg border bg-card p-3",
                    date === todayIso && "border-primary",
                  )}
                >
                  <p className="text-sm font-semibold">
                    {WEEKDAYS[mondayIndex(focus.y, focus.m, d)]} {d} {MONTHS[focus.m - 1].slice(0, 3)}
                    {date === todayIso && (
                      <span className="ml-2 text-xs font-medium text-primary">Today</span>
                    )}
                  </p>
                  {holiday && (
                    <p className="mt-0.5 text-sm text-amber-700">
                      {holiday.optional
                        ? `Optional: ${holiday.name}${holiday.picked ? " (you)" : ""}`
                        : holiday.name}
                    </p>
                  )}
                  {entries.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {entries.map((e) => (
                        <li
                          key={e.key}
                          className={cn(
                            "rounded px-1.5 py-0.5 text-xs",
                            e.kind === "leave"
                              ? "bg-primary/10 text-primary"
                              : e.kind === "optional"
                                ? "bg-amber-500/10 text-amber-800"
                                : "bg-emerald-500/10 text-emerald-700",
                            e.pending && "border border-dashed border-current bg-transparent",
                          )}
                        >
                          {e.who} · {e.what}
                          {e.pending && <span className="sr-only"> (pending)</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
          <Card className="hidden sm:block">
            <CardContent className="overflow-x-auto p-2 sm:p-4">
              <div className="grid min-w-[42rem] grid-cols-7 gap-px rounded-md bg-border">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="bg-muted/50 px-2 py-1 text-xs font-medium text-muted-foreground">
                    {w}
                  </div>
                ))}
                {cells.map((d, i) => {
                  if (d === null) return <div key={`x${i}`} className="min-h-24 bg-muted/20" />;
                  const date = iso(focus.y, focus.m, d);
                  const entries = byDate.get(date) ?? [];
                  const holiday = holidays.get(date);
                  const off = (weeklyOff & weekdayBit(focus.y, focus.m, d)) !== 0;
                  return (
                    <div
                      key={date}
                      className={cn(
                        "min-h-24 space-y-1 bg-background p-1.5",
                        (off || (holiday && (!holiday.optional || holiday.picked))) && "bg-muted/40",
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={cn(
                            "text-xs font-medium",
                            date === todayIso && "rounded-full bg-primary px-1.5 text-primary-foreground",
                          )}
                        >
                          {d}
                        </span>
                        {off && !holiday && <span className="text-[10px] text-muted-foreground">Off</span>}
                      </div>
                      {holiday && (
                        <p className="truncate text-[11px] text-amber-800">
                          {holiday.optional
                            ? `Optional: ${holiday.name}${holiday.picked ? " (you)" : ""}`
                            : holiday.name}
                        </p>
                      )}
                      {entries.slice(0, 4).map((e) => (
                        <p
                          key={e.key}
                          title={`${e.who} · ${e.what}${e.pending ? " (pending)" : ""}`}
                          className={cn(
                            "truncate rounded px-1 text-[11px]",
                            e.kind === "leave"
                              ? "bg-primary/10 text-primary"
                              : e.kind === "optional"
                                ? "bg-amber-500/10 text-amber-800"
                                : "bg-emerald-500/10 text-emerald-700",
                            e.pending && "border border-dashed border-current bg-transparent",
                          )}
                        >
                          {e.who} · {e.what}
                        </p>
                      ))}
                      {entries.length > 4 && (
                        <p className="text-[11px] text-muted-foreground">+{entries.length - 4} more</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          {entriesThisMonth === 0 && (
            <EmptyState
              icon={CalendarDays}
              title="Nobody is away this month"
              description="No approved leave or WFH in your view for this month."
            />
          )}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-primary/10" /> Leave
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-emerald-500/10" /> Work from home
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-amber-500/10" /> Optional holiday (picked)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border border-dashed border-muted-foreground" /> Pending
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-muted" /> Weekly off / holiday
            </span>
          </div>
        </>
      )}
    </div>
  );
}
